import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Tent, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [meta, setMeta] = useState({ golongan: [], kwarran: [] });
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    identifier: "", password: "", name: "", email: "", nta: "",
    role: "pramuka", golongan: "", gudep: "", kwarran: "", tanggal_dilantik: "", jenis_dewasa: "Pembina",
  });
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const u = await login(form.identifier, form.password);
        toast.success("Selamat datang kembali!");
        redirect(u);
      } else {
        const payload = {
          name: form.name, email: form.email, password: form.password, nta: form.nta, role: form.role,
        };
        if (form.role === "pramuka") {
          Object.assign(payload, {
            golongan: form.golongan || null, gudep: form.gudep || null,
            kwarran: form.kwarran || null, tanggal_dilantik: form.tanggal_dilantik || null,
          });
        } else {
          payload.gudep = form.gudep || null;
          payload.kwarran = form.kwarran || null;
          payload.jenis_dewasa = form.jenis_dewasa;
        }
        const u = await register(payload);
        toast.success("Pendaftaran berhasil!");
        redirect(u);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/profil";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const redirect = (u) => {
    if (u.role === "pramuka") navigate("/profil");
    else navigate("/dashboard");
  };

  const inputCls = "w-full rounded-lg border border-[#E5DFD7] bg-white px-3.5 py-2.5 text-sm text-[#1F1917] outline-none focus:border-[#E5A93C] focus:ring-2 focus:ring-[#E5A93C]/30 transition";
  const labelCls = "block text-xs uppercase tracking-widest font-bold text-[#8C827A] mb-1.5";

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-[#2A1F1B] p-12 text-white relative overflow-hidden">
        <div className="relative z-10 flex items-center gap-2.5">
          <div className="h-10 w-10 rounded-xl bg-[#E5A93C] flex items-center justify-center">
            <Tent className="h-5 w-5 text-[#2A1F1B]" />
          </div>
          <div className="font-display font-extrabold text-lg">SIPRAM DEPOK</div>
        </div>
        <div className="relative z-10">
          <h2 className="font-display text-4xl font-extrabold leading-tight">Satu Data Pramuka<br />Kota Depok</h2>
          <p className="mt-4 text-white/70 max-w-md leading-relaxed">
            Kelola data anggota dan prestasi Gerakan Pramuka se-Kota Depok dalam satu platform terintegrasi dan resmi.
          </p>
        </div>
        <div className="relative z-10 text-white/40 text-sm">© 2026 Kwarcab Kota Depok</div>
        <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-[#E5A93C]/20" />
        <div className="absolute top-20 -right-10 h-40 w-40 rounded-full bg-[#B85C38]/20" />
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 grain">
        <div className="w-full max-w-md">
          <div className="flex bg-[#F7F4EF] rounded-xl p-1 mb-8">
            <button data-testid="auth-login-tab" onClick={() => setMode("login")}
              className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition ${mode === "login" ? "bg-white shadow-sm text-[#4A2E2B]" : "text-[#8C827A]"}`}>
              Masuk
            </button>
            <button data-testid="auth-register-tab" onClick={() => setMode("register")}
              className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition ${mode === "register" ? "bg-white shadow-sm text-[#4A2E2B]" : "text-[#8C827A]"}`}>
              Daftar
            </button>
          </div>

          <h1 className="font-display text-2xl font-extrabold text-[#1F1917] mb-1">
            {mode === "login" ? "Masuk ke akun Anda" : "Buat akun baru"}
          </h1>
          <p className="text-sm text-[#8C827A] mb-6">
            {mode === "login" ? "Gunakan Email atau NTA Anda." : "Lengkapi data untuk mulai mencatat prestasi."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "login" ? (
              <>
                <div>
                  <label className={labelCls}>Email atau NTA</label>
                  <input data-testid="auth-input-email-nta" className={inputCls} value={form.identifier}
                    onChange={(e) => upd("identifier", e.target.value)} required placeholder="email@contoh.com / NTA" />
                </div>
                <div>
                  <label className={labelCls}>Password</label>
                  <input data-testid="auth-input-password" type="password" className={inputCls} value={form.password}
                    onChange={(e) => upd("password", e.target.value)} required placeholder="••••••••" />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className={labelCls}>Daftar sebagai</label>
                  <select data-testid="auth-select-role" className={inputCls} value={form.role}
                    onChange={(e) => upd("role", e.target.value)}>
                    <option value="pramuka">Pramuka (Anggota)</option>
                    <option value="pembina">Pembina / Pelatih</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Nama Lengkap</label>
                  <input data-testid="auth-input-name" className={inputCls} value={form.name}
                    onChange={(e) => upd("name", e.target.value)} required placeholder="Nama lengkap" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Email</label>
                    <input data-testid="auth-input-email" type="email" className={inputCls} value={form.email}
                      onChange={(e) => upd("email", e.target.value)} required placeholder="email@contoh.com" />
                  </div>
                  <div>
                    <label className={labelCls}>NTA</label>
                    <input data-testid="auth-input-nta" className={inputCls} value={form.nta}
                      onChange={(e) => upd("nta", e.target.value)} required placeholder="Nomor Tanda Anggota" />
                  </div>
                </div>
                {form.role === "pramuka" && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelCls}>Golongan</label>
                        <select data-testid="auth-select-golongan" className={inputCls} value={form.golongan}
                          onChange={(e) => upd("golongan", e.target.value)}>
                          <option value="">Pilih</option>
                          {meta.golongan.map((g) => <option key={g} value={g}>{g}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>Kwarran</label>
                        <select data-testid="auth-select-kwarran" className={inputCls} value={form.kwarran}
                          onChange={(e) => upd("kwarran", e.target.value)}>
                          <option value="">Pilih</option>
                          {meta.kwarran.map((k) => <option key={k} value={k}>{k}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className={labelCls}>Gugus Depan (Gudep)</label>
                      <input className={inputCls} value={form.gudep}
                        onChange={(e) => upd("gudep", e.target.value)} placeholder="cth: SMPN 1 Depok" />
                    </div>
                    <div>
                      <label className={labelCls}>Tanggal Dilantik</label>
                      <input type="date" className={inputCls} value={form.tanggal_dilantik}
                        onChange={(e) => upd("tanggal_dilantik", e.target.value)} />
                    </div>
                  </>
                )}
                {form.role === "pembina" && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelCls}>Jenis</label>
                        <select data-testid="auth-select-jenis-dewasa" className={inputCls} value={form.jenis_dewasa}
                          onChange={(e) => upd("jenis_dewasa", e.target.value)}>
                          <option value="Pembina">Pembina</option>
                          <option value="Pelatih">Pelatih</option>
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>Kwarran</label>
                        <select data-testid="auth-select-kwarran-dewasa" className={inputCls} value={form.kwarran}
                          onChange={(e) => upd("kwarran", e.target.value)}>
                          <option value="">Pilih</option>
                          {meta.kwarran.map((k) => <option key={k} value={k}>{k}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className={labelCls}>Gugus Depan yang Dibina</label>
                      <input className={inputCls} value={form.gudep}
                        onChange={(e) => upd("gudep", e.target.value)} placeholder="cth: SMPN 1 Depok" />
                    </div>
                  </>
                )}
                <div>
                  <label className={labelCls}>Password</label>
                  <input data-testid="auth-input-password" type="password" className={inputCls} value={form.password}
                    onChange={(e) => upd("password", e.target.value)} required minLength={6} placeholder="Min. 6 karakter" />
                </div>
              </>
            )}

            <button data-testid="auth-submit-btn" type="submit" disabled={loading}
              className="w-full bg-[#4A2E2B] text-white py-3 rounded-lg font-bold hover:bg-[#3a2320] transition disabled:opacity-60 flex items-center justify-center gap-2">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {mode === "login" ? "Masuk" : "Daftar Sekarang"}
            </button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-[#E5DFD7]" />
            <span className="text-xs uppercase tracking-widest font-bold text-[#8C827A]">atau</span>
            <div className="flex-1 h-px bg-[#E5DFD7]" />
          </div>
          <button data-testid="auth-google-btn" type="button" onClick={googleLogin}
            className="w-full bg-white border border-[#E5DFD7] text-[#1F1917] py-3 rounded-lg font-bold hover:bg-[#F7F4EF] transition flex items-center justify-center gap-3">
            <svg className="h-5 w-5" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.8 6C12.3 13.1 17.7 9.5 24 9.5z" />
              <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z" />
              <path fill="#FBBC05" d="M10.4 28.7c-.5-1.5-.8-3-.8-4.7s.3-3.2.8-4.7l-7.8-6C.9 16.5 0 20.1 0 24s.9 7.5 2.6 10.7l7.8-6z" />
              <path fill="#34A853" d="M24 48c6.2 0 11.6-2 15.4-5.6l-7.5-5.8c-2.1 1.4-4.8 2.3-7.9 2.3-6.3 0-11.7-3.6-13.6-8.8l-7.8 6C6.5 42.6 14.6 48 24 48z" />
            </svg>
            {mode === "login" ? "Masuk dengan Google" : "Daftar dengan Google"}
          </button>
        </div>
      </div>
    </div>
  );
}
